/// <amd-module name="VSS/Ext/Components/ExternalContent/SdkInteractionProvider" />
import { Observable } from "VSS/Core/Observable";
import * as Resources from "VSS/Ext/Resources";
import { ILegacyPlatformService } from "VSS/Legacy/Types";
import { IAuthenticationService } from "VSS/Platform/Authentication";
import { IVssPageContext, RemoteSerializationSettingsKey } from "VSS/Platform/Context";
import { DelegatedAppTokenType, WebSessionToken } from "VSS/Platform/Contracts/Authentication";
import { IContributedService, IContributionProviderDetails, IVssContributionService } from "VSS/Platform/Contribution";
import { ClientTheme, IVssThemeService } from "VSS/Platform/Theming";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { getExtensionBaseUrl, getExtensionIdentifier, getExternalPageContext } from "../../ExternalContentUtil";
import { channelManager, IXDMChannel } from "../../Xdm";
import {
    ExternalContentResizeOptions,
    IExternalContentInteractionProvider,
    IExternalContentLoadedEvent,
    IExternalContentLoadingErrorEvent,
    IExternalContentResizedEvent
} from "./ExternalContent.Props";

/**
 * Data passed to the host from an extension frame via the initial handshake
 */
interface IExtensionHandshakeData {
    /**
     * Unless false, consider the extension loaded upon completion of the initial handshake.
     */
    loaded?: boolean;

    /**
     * Unless false, send back the theme data as part of the initial handshake
     */
    applyTheme?: boolean;

    /**
     * The version of the DevOps.SDK javascript file being used by the extension
     */
    sdkVersion?: number;
}

/**
 * Data passed to the host from an extension frame via the initial handshake from the legacy extension model
 */
interface IExtensionHandshakeDataLegacy {
    /**
     * If true, consider the extension loaded upon completion of the initial handshake.
     */
    notifyLoadSucceeded?: boolean;

    /**
     * If true, send back the theme data as part of the initial handshake
     */
    applyTheme?: boolean;

    /**
     * The version of the VSS.SDK javascript file being used by the extension
     */
    vssSDKVersion: number;
}

const enum LoadTimeouts {
    SlowLoad = 10000
}

/**
 * Interaction provider for extensions that leverage the VSS SDK to communicate back to the parent frame
 */
export class SdkInteractionProvider
    extends Observable<
        IExternalContentLoadedEvent | IExternalContentLoadingErrorEvent | IExternalContentResizedEvent,
        "loaded" | "loadError" | "resized"
    >
    implements IExternalContentInteractionProvider {
    private xdmChannel: IXDMChannel | undefined;
    private contributionProvider: IContributionProviderDetails;
    private slowLoadTimeout: number;
    private disposed: boolean;
    private sdkVersion: number;
    private themed: boolean;
    private legacyHostManagementPromise: Promise<any>;

    constructor(
        private pageContext: IVssPageContext,
        private iframe: HTMLIFrameElement,
        private contributionId: string,
        private initialHandshakeData: object | undefined,
        private resizeOptions: ExternalContentResizeOptions = ExternalContentResizeOptions.Default,
        private backgroundHost: boolean = false
    ) {
        super();

        this.xdmChannel = channelManager.addChannel(iframe.contentWindow!, iframe.src, this.pageContext);

        const hostControl = this.getHostControl();
        this.xdmChannel.getObjectRegistry().register("DevOps.HostControl", hostControl);
        this.xdmChannel.getObjectRegistry().register("VSS.HostControl", hostControl);

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        this.contributionProvider = contributionService.getContributionProvider(this.contributionId)!;

        this.slowLoadTimeout = window.setTimeout(() => {
            this.notify(
                {
                    messageFormat: Resources.slowLoadWarning,
                    extensionName: this.contributionProvider.displayName,
                    extensionId: this.contributionProvider.id,
                    loadComplete: false
                },
                "loadError"
            );
        }, LoadTimeouts.SlowLoad);
    }

    public dispose() {
        if (this.xdmChannel) {
            channelManager.removeChannel(this.xdmChannel);
            this.xdmChannel = undefined;
        }
        window.clearTimeout(this.slowLoadTimeout);
        if (this.themed) {
            const themeService = this.pageContext.getService<IVssThemeService>("IVssThemeService");
            themeService.unsubscribe(this.onThemeChanged, "themeChanged");
        }
        this.disposed = true;
    }

    private delayedNotifyLoaded() {
        // Delay this call to allow the handshake message to be sent back to the extension frame before declaring that the load is done.
        window.setTimeout(() => {
            if (!this.disposed) {
                this.notifyLoaded();
            }
        }, 0);
    }

    private notifyLoaded = (): void => {
        window.clearTimeout(this.slowLoadTimeout);
        this.notify({ proxy: this.xdmChannel, themed: this.themed, sdkVersion: this.sdkVersion }, "loaded");
    };

    private notifyLoadFailed = (error: Error | string): void => {
        window.clearTimeout(this.slowLoadTimeout);
        this.notify(
            {
                messageFormat: Resources.extensionError + " " + (error && (error as Error).message ? (error as Error).message : (error as string)),
                extensionName: this.contributionProvider.displayName,
                extensionId: this.contributionProvider.id,
                loadComplete: true,
                error: error as Error
            },
            "loadError"
        );
    };

    private onThemeChanged = (value: ClientTheme) => {
        if (this.xdmChannel) {
            // Fire an event in the extension iframe that the external content can listen to in order to update its themed values.
            this.xdmChannel.invokeRemoteMethod("dispatchEvent", "DevOps.SdkClient", ["themeChanged", { detail: value }]);
        }
    };

    private getLegacyHostManagementService(): Promise<any> {
        if (!this.legacyHostManagementPromise) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.legacyHostManagementPromise = contributionService.getContributionsAsync(["ms.vss-web.legacy-platform-component"]).then(() => {
                const legacyService = this.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
                return legacyService.requireModules(["VSS/SDK/Host"]).then(([sdkHostModule]) => {
                    const baseUri = getExtensionBaseUrl(this.pageContext, this.contributionId);
                    const extensionIdentifier = getExtensionIdentifier(this.contributionProvider.id);
                    const publisherId = extensionIdentifier.publisherId;
                    const extensionId = extensionIdentifier.extensionId;

                    const hostManagementService = new sdkHostModule.HostManagementService({
                        publisherId,
                        extensionId,
                        baseUri,
                        contributionId: this.contributionId,
                        version: this.contributionProvider.version,
                        initialConfig: this.initialHandshakeData
                    });

                    hostManagementService[RemoteSerializationSettingsKey] = { useLegacySerialization: true };

                    return hostManagementService;
                });
            });
        }
        return this.legacyHostManagementPromise;
    }

    private getHostControl() {
        return {
            [RemoteSerializationSettingsKey]: { proxyFunctions: true },
            resize: (width: number, height: number) => {
                if ((this.resizeOptions & ExternalContentResizeOptions.FixedHeight) === 0 && height !== undefined) {
                    this.iframe.style.height = height + "px";
                }
                if ((this.resizeOptions & ExternalContentResizeOptions.FixedWidth) === 0 && width !== undefined) {
                    this.iframe.style.width = width + "px";
                }
                this.notify({ height, width }, "resized");
            },
            initialHandshake: (data: IExtensionHandshakeData | IExtensionHandshakeDataLegacy) => {
                if (data) {
                    this.sdkVersion = (data as IExtensionHandshakeData).sdkVersion || 0;
                    const useNewSdk = this.sdkVersion > 0;

                    let themeData: { [varName: string]: string } | undefined;
                    this.themed = useNewSdk && !this.backgroundHost ? data.applyTheme !== false : data.applyTheme === true;

                    if (this.themed) {
                        const themeService = this.pageContext.getService<IVssThemeService>("IVssThemeService");
                        themeData = themeService.getCurrentTheme().data;
                        themeService.subscribe(this.onThemeChanged, "themeChanged");
                    }

                    if (useNewSdk) {
                        // New extension model
                        const handshakeData = data as IExtensionHandshakeData;
                        if (handshakeData.loaded !== false) {
                            this.delayedNotifyLoaded();
                        }

                        this.xdmChannel!.getObjectRegistry().register("DevOps.ServiceManager", {
                            [RemoteSerializationSettingsKey]: { proxyFunctions: true },
                            getService: (serviceContributionId: string) => {
                                const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
                                return contributionService
                                    .getContributionAsync<IContributedService>(serviceContributionId)
                                    .then(contributedService => {
                                        if (contributedService) {
                                            // New platform service contributions must have serviceName
                                            if (contributedService.serviceName) {
                                                return contributionService.getServiceEx(serviceContributionId, contributedService, true);
                                            } else {
                                                // Legacy platform services will have a content.require property
                                                const legacyContributedService = contributedService as any;
                                                if (legacyContributedService.content && legacyContributedService.content.require) {
                                                    return this.getLegacyHostManagementService().then(hostMgmtSvc => {
                                                        return hostMgmtSvc.getServiceContribution(serviceContributionId).then((svcContrib: any) => {
                                                            return svcContrib.getInstance().then((instance: any) => {
                                                                if (instance) {
                                                                    instance[RemoteSerializationSettingsKey] = { useLegacySerialization: true };
                                                                }
                                                                return instance;
                                                            });
                                                        });
                                                    });
                                                }
                                            }
                                        }

                                        // Service contribution not found
                                        return Promise.reject(undefined);
                                    });
                            }
                        });

                        return {
                            contributionId: this.contributionId,
                            initialConfig: this.initialHandshakeData,
                            context: getExternalPageContext(this.pageContext, this.contributionId),
                            themeData
                        };
                    } else {
                        // Old extension model. Pull in legacy content
                        const handshakeData = data as IExtensionHandshakeDataLegacy;
                        return this.getLegacyHostManagementService().then(hostManagementService => {
                            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
                            const legacyService = this.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
                            return legacyService.getLegacyPageContext(true).then(legacyPageContext => {
                                if (handshakeData.notifyLoadSucceeded) {
                                    this.delayedNotifyLoaded();
                                }

                                this.xdmChannel!.getObjectRegistry().register("vss.hostManagement", hostManagementService);

                                const baseUri = getExtensionBaseUrl(this.pageContext, this.contributionId);
                                const extensionIdentifier = getExtensionIdentifier(this.contributionProvider.id);
                                const publisherId = extensionIdentifier.publisherId;
                                const extensionId = extensionIdentifier.extensionId;

                                let initialConfig: any = this.initialHandshakeData;
                                if (typeof initialConfig === "object") {
                                    initialConfig = { ...initialConfig };
                                    initialConfig[RemoteSerializationSettingsKey] = { useLegacySerialization: true };
                                }

                                return {
                                    pageContext: legacyPageContext,
                                    initialConfig,
                                    extensionContext: {
                                        publisherId,
                                        extensionId,
                                        baseUri,
                                        version: this.contributionProvider.version
                                    },
                                    contribution: {
                                        id: this.contributionId,
                                        properties: contributionService.getContribution(this.contributionId)
                                    },
                                    themeData
                                };
                            });
                        });
                    }
                }
            },
            notifyLoadSucceeded: this.notifyLoaded,
            notifyLoadFailed: this.notifyLoadFailed,
            getAccessToken: (): Promise<WebSessionToken> => {
                if (userHasClaim(this.pageContext, UserClaims.Member)) {
                    const authenticationService = this.pageContext.getService<IAuthenticationService>("IAuthenticationService");
                    return authenticationService
                        .getExtensionTokenProvider(this.contributionProvider.id, DelegatedAppTokenType.Session)
                        .getSessionToken();
                } else {
                    return Promise.resolve({} as WebSessionToken);
                }
            },
            getAppToken: (): Promise<WebSessionToken> => {
                if (userHasClaim(this.pageContext, UserClaims.Member)) {
                    const authenticationService = this.pageContext.getService<IAuthenticationService>("IAuthenticationService");
                    return authenticationService.getExtensionTokenProvider(this.contributionProvider.id, DelegatedAppTokenType.App).getSessionToken();
                } else {
                    return Promise.resolve({} as WebSessionToken);
                }
            }
        };
    }
}
